/**
 * @fileoverview Which URL to draw a plant photo from.
 *
 * The API sends up to four ways to reach the same picture — a signed original,
 * an 800x600 card, a 200x200 thumbnail, and a legacy plain url — and not every
 * photo has all four. Three components were each picking between them with
 * their own slightly different ladder. This is that ladder, once.
 *
 * Where: plant pictures: the card's photo (PlantCardPhoto), tiles and rows
 * (plantCoverUrl), the bed's plant rows, Seasons, ShareMoments, the
 * profile's photo.
 * API: none; it chooses among the photo's signed_photo_url, cardUrl,
 * thumbUrl and url.
 * Watch out: a signed original always wins when the photo has one, whatever
 * size was asked for.
 *
 * @module plantPhotoUrl
 */

import { Plant } from '@/shared/types/plant';
import { PlantPhoto } from '@/shared/types/photo';

/**
 * The best available URL for a photo at the size asked for.
 *
 * Falls back rather than failing: an old photo with only an original still
 * draws, just larger than it needed to be.
 *
 * @param photo - The photo, or nothing.
 * @param size - The variant wanted.
 * @returns A URL, or the empty string when there is nothing to draw.
 */
export function photoUrl(
  photo: PlantPhoto | undefined | null,
  size: 'card' | 'thumb' | 'original' = 'card'
): string {
  if (!photo) return '';
  if (photo.signed_photo_url) return photo.signed_photo_url;
  if (size === 'card' && photo.cardUrl) return photo.cardUrl;
  if (size === 'thumb' && photo.thumbUrl) return photo.thumbUrl;
  return photo.url || '';
}

/**
 * The picture that stands for a plant in a list or a tile.
 *
 * The newest one, which is how the API already orders them — a plant is best
 * recognised by what it looks like now, not what it looked like as a seed
 * tray.
 *
 * @param plant - The plant to find a face for.
 * @param size - The variant wanted.
 * @returns A URL, or '' for a plant with no photo.
 */
export function plantCoverUrl(plant: Plant, size: 'card' | 'thumb' = 'thumb'): string {
  const photos = (plant.photos ?? []) as unknown as PlantPhoto[];
  return photoUrl(photos[0], size);
}
